import { Building2, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import { Skeleton } from '../../../../components/Skeleton';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import type { MenuItem } from '../../../../components/Menu';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { PpmpModal } from '../components/PpmpModal';
import { ppmpsApi } from '../ppmpsApi';
import type { PpmpListItem } from '../ppmpTypes';
import { annualProcurementPlansApi } from '../procurementPlansApi';
import type {
  AnnualProcurementPlanDetail,
  AppDepartmentRow,
} from '../procurementPlanTypes';

/**
 * One department's PPMPs under one annual procurement plan.
 *
 * **This is a ROUTE, and that is the whole point of it.** The budget plan's own roster shipped
 * its second level as a drill-down inside the panel on 21 Sep 2026 and it was replaced the same
 * day: a level with no URL cannot be linked to a colleague, bookmarked, opened in a second tab,
 * or returned to with the browser's own Back.
 *
 * **It is the HEADER PROFILE LAYOUT with no tabs, and NOT `<IndexPage>`.** That shell owes the
 * server-side list contract — a paged `ListQuery`, facets, a filter rail — and this list is
 * scoped to one plan and one department, which is a far narrower question than a module index.
 * What the header layout gives it is the strip, the banner, the inset and the scroll region,
 * none of which needs a stored record to be true of.
 *
 * **There is no profile behind a roster row**, which is why the header states the department
 * rather than linking to one: every figure on that row is computed at read time.
 */
export function AppDepartmentPage() {
  const { planId, orgUnitId } = useParams<{ planId: string; orgUnitId: string }>();
  const navigate = useNavigate();

  const [plan, setPlan] = useState<AnnualProcurementPlanDetail | null>(null);
  const [department, setDepartment] = useState<AppDepartmentRow | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore';
    ppmp: PpmpListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const [showDeleted, setShowDeleted] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    The PLAN and the ROSTER, because this page needs both and neither is derivable from the
    other: the plan carries the number and the submission window, and the roster is the only
    thing that can name a department which has filed NOTHING — which is the one a reader most
    often arrives here to start.
  */
  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!planId || !orgUnitId) return;

      setLoading(true);

      Promise.all([
        annualProcurementPlansApi.get(planId, signal),
        annualProcurementPlansApi.roster(planId, signal),
      ])
        .then(([nextPlan, roster]) => {
          if (signal?.aborted) return;

          setPlan(nextPlan);
          setDepartment(roster.find((row) => row.orgUnitId === orgUnitId) ?? null);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError
              ? caught
              : new ApiError('Could not load this department.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [planId, orgUnitId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /*
    SERVER-side, unlike the roster one level up: that list arrives whole with the record, and
    this one is a module's own records scoped by two ids. The endpoint pages it.
  */
  const fetchPpmps = useCallback(
    async (query: DataTableQuery, signal?: AbortSignal) => {
      if (!planId || !orgUnitId) {
        return { items: [], total: 0, page: query.page, pageSize: query.pageSize };
      }

      const result = await ppmpsApi.list(
        {
          annualProcurementPlanId: planId,
          orgUnitId,
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: showDeleted ? undefined : 'active',
        },
        signal,
      );

      return {
        items: result.items,
        total: result.total,
        page: result.page,
        pageSize: result.pageSize,
      };
    },
    // `reloadKey` is a dependency so a write re-runs the fetch rather than serving a list the
    // reader's own record is missing from.
    [planId, orgUnitId, showDeleted, reloadKey],
  );

  const state = useDataTable<PpmpListItem>({ fetchPage: fetchPpmps, defaultSort: 'ppmpNumber' });

  const reloadPpmps = state.reload;

  /** A write moves the department's own roster figures too, so both are re-read. */
  const refresh = useCallback(() => {
    setReloadKey((current) => current + 1);
    reloadPpmps();
    load();
  }, [reloadPpmps, load]);

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setActionError(null);

    try {
      if (confirming.kind === 'delete') await ppmpsApi.deactivate(confirming.ppmp.id);
      else await ppmpsApi.activate(confirming.ppmp.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setActionError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  const rowActions = useCallback(
    (ppmp: PpmpListItem): MenuItem[] => [
      {
        key: `restore-${ppmp.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'ppmps.delete',
        disabledReason: ppmp.isActive ? 'This PPMP is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', ppmp }),
      },
    ],
    [],
  );

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Procurement Planning',
    {
      label: 'Annual Procurement Plan',
      to: '/procurement/procurement-planning/annual-procurement-plans',
    },
    ...(plan
      ? [
          {
            label: plan.appNumber,
            to: `/procurement/procurement-planning/annual-procurement-plans/${plan.id}`,
          },
        ]
      : []),
    department?.orgUnitName ?? 'Department',
  ];

  if (error && !plan) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading && !plan) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!plan) return null;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="app-department"
        header={
          <ProfileHeader
            id="app-department-header"
            icon={<Building2 className="h-6 w-6" aria-hidden="true" />}
            title={department?.orgUnitName ?? 'Department'}
            meta={[
              'Project Procurement Management Plans',
              plan.appNumber,
              `FY ${plan.fiscalYear}`,
            ]}
            secondary={
              department
                ? [
                    department.progressLabel,
                    `${department.lineItemCount} line item${department.lineItemCount === 1 ? '' : 's'}`,
                    `${formatMoney(department.estimatedBudget)} planned`,
                  ]
                : []
            }
            /*
              NO settings cog: there is no record here to act on. The page is a VIEW of one
              department's filing under one plan — every figure on it is derived — so the only
              verbs belong to the PPMPs in the table below, where their own menus carry them.
            */
          />
        }
        panel="bleed"
      >
        <div className="flex h-full min-w-0 flex-col p-4">
          <DataTablePanel<PpmpListItem>
            id="app-department-ppmps"
            state={state}
            columns={COLUMNS}
            rowKey={(ppmp) => ppmp.id}
            fill
            otherInfo={(ppmp) => ppmp}
            onRowOpen={(ppmp) => navigate(`/procurement/procurement-planning/ppmps/${ppmp.id}`)}
            openPermission="ppmps.read"
            rowActions={rowActions}
            onEdit={undefined}
            onDelete={(ppmp) => setConfirming({ kind: 'delete', ppmp })}
            deletePermission="ppmps.delete"
            deleteDisabledReason={(ppmp) =>
              ppmp.isActive ? null : 'This PPMP is already deleted.'
            }
            onCreate={() => setCreating(true)}
            createPermission="ppmps.create"
            createDisabledReason={
              plan.isSubmissionOpen
                ? department
                  ? null
                  : 'This department is not enrolled to file a PPMP.'
                : 'The submission window for this plan has closed.'
            }
            /*
              A panel offering Show deleted OWES a `status: true` column — otherwise the
              deleted rows arrive wearing no marker at all, which is the one state a row must
              never be silent about.
            */
            settingsItems={[
              {
                key: 'show-deleted',
                label: 'Show deleted',
                checked: showDeleted,
                onClick: () => setShowDeleted((v) => !v),
              },
            ]}
            searchPlaceholder="Search PPMPs, PPAs or titles"
            emptyMessage="This department has filed nothing under this plan yet."
            emptySearchMessage="No PPMP matches that search."
          />
        </div>
      </ProfileHeaderShell>

      {creating && department ? (
        <PpmpModal
          open
          plan={plan}
          department={department}
          editing={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete PPMP' : 'Restore PPMP'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`app-department-ppmp-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'delete'
                  ? `${confirming.ppmp.ppmpNumber} is hidden from this list and stops counting toward the department's planned total.`
                  : `${confirming.ppmp.ppmpNumber} returns to this list.`}
              </p>

              {confirming.kind === 'delete' ? (
                <p>
                  Its {confirming.ppmp.lineItemCount} procurement line
                  {confirming.ppmp.lineItemCount === 1 ? '' : 's'} are kept exactly as they are,
                  so a restore brings the plan back with its contents. The PPMP number is never
                  reused.
                </p>
              ) : null}

              {actionError ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}

const COLUMNS: DataColumn<PpmpListItem>[] = [
  {
    key: 'ppmpNumber',
    header: 'PPMP no.',
    width: 160,
    sortable: true,
    render: (ppmp) => <TextCell value={ppmp.ppmpNumber} mono />,
  },
  {
    key: 'title',
    header: 'Title',
    width: 300,
    sortable: true,
    render: (ppmp) => <TextCell value={ppmp.title} />,
  },
  {
    key: 'ppaNumber',
    header: 'PPA',
    width: 150,
    sortable: true,
    summary: true,
    render: (ppmp) => <TextCell value={ppmp.ppaNumber} mono />,
  },
  {
    key: 'ppaLineItemName',
    header: 'Budgeted line',
    width: 240,
    // NULL where the plan names no line — an em dash, which is what an absent value reads as.
    render: (ppmp) => <TextCell value={ppmp.ppaLineItemName} />,
  },
  {
    key: 'lineItemCount',
    header: 'Lines',
    width: 90,
    align: 'right',
    summary: true,
    render: (ppmp) => <TextCell value={String(ppmp.lineItemCount)} mono numeric />,
  },
  {
    key: 'estimatedBudget',
    header: 'Estimated budget',
    width: 170,
    align: 'right',
    footer: (rows) => formatMoney(rows.reduce((sum, row) => sum + row.estimatedBudget, 0)),
    render: (ppmp) => <TextCell value={formatMoney(ppmp.estimatedBudget)} mono numeric />,
  },
  {
    key: 'postingDate',
    header: 'Posting',
    width: 130,
    // A CALENDAR date, so `formatDateOnly` through a mono TextCell — never `<DateCell>`.
    render: (ppmp) => (
      <TextCell value={ppmp.postingDate ? formatDateOnly(ppmp.postingDate) : null} mono />
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (ppmp) =>
      ppmp.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
